/* eslint-disable require-await */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { Unsubscribe } from "firebase/database";
import {
  IOptionSupport,
  IPictureItem,
  INamedItem,
  IControl,
  IOwnerProfileSession,
  ControlRecord,
  IVoteTask,
  VoteTask,
  ISessionSettings,
  IPilloryTopic,
  PilloryTopic,
  IOwnerHistory,
  IUserRecord,
  IShowNotificationState,
  LovenseCommand,
} from "../ts/Definitions";
import Database from "../utilities/database";
import {
  subscribe,
  unsubscribe,
  decrypt,
  memberOwnerKeySuffix,
} from "../utilities/utils";
import BaseSettings from "./BaseSettings";
import debounce from 'lodash/debounce';
import {
  Auth,
  UserCredential,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { ProfileRole } from "../ts/enums";
import { getProfileImage } from "../utilities/imageUtils";
import { MessageType, NotificationType } from "./messaging";
import Cookies from "../utilities/cookies";
import {
  handleDiscordPilloryTickerDown,
  handleDiscordPilloryTickerUp,
  handleDiscordVoteTickerDown,
  handleDiscordVoteTickerUp,
} from "../utilities/discord";
import { pushLovense } from "../utilities/externalApiUtils";
import { v1 } from "uuid";
import { isAfter, startOfYesterday } from "date-fns";

class GuestSettings extends BaseSettings {
  constructor(database: Database, fbAuth: Auth) {
    super(database);
    this.fbAuth = fbAuth;
  }

  initialise = (
    cookiesService: Cookies,
    showMessage: (args: IShowNotificationState) => void
  ) => {
    this.showMessage = showMessage;
    this.cookiesService = cookiesService;
    void this.loginGuest().then(() => {
      this.loggedIn = true;
      this.database.snapAndSubAllControl(this.onAllControlsChanged);
      this.unsubscribePillory = this.database.snapAndSubPillory(
        this.onPilloryChanged
      );
      this.unsubscribeVotingTasks = this.database.snapAndSubVoteTasks(
        this.onVotingTasks,
        v1()
      );
      this.unsubscribeOwnerProfiles = this.database.snapAndSubOwnerProfiles(
        undefined,
        this.ownerProfilesChangedCallback
      );
    });
  };
  private cookiesService: Cookies = {} as Cookies;
  private loggedIn = false;
  private fbAuth: Auth = {} as Auth;
  public showMessage: (args: IShowNotificationState) => void = () => ({});

  // logs the user on ans if not already set up it initialised the user with new settings. if user is not registering and user id not found then false returned, likewise if registering and user already exists then false returned
  // called via log on screen and also via initialisation method
  private loginGuest = async (): Promise<void> => {
    try {
      if (import.meta.env.VITE_LOCAL_MODE === 'true') {

        return;
      }
      await signInWithEmailAndPassword(
        this.fbAuth,
        `${import.meta.env.VITE_GUEST_NAME}@tarlings.com`,
        import.meta.env.VITE_GUEST_TOKEN as string
      )
        .then(function (userCredential: UserCredential) {
          return userCredential;
        })
        .catch(function (error: { code: string; message: string }) {
          // Handle Errors here.
          if (error.code === "auth/user-not-found") {
            return undefined;
          }
          throw error;
        });
    } catch (e: any) {
      console.log(e);
    }
  };

  private unsubscribeVotingTasks: Unsubscribe | undefined;
  private votingTasksCache: VoteTask[] | undefined;
  private votingTasksChangedListeners: (() => void)[] = [];
  private allControlRecords: Record<string, ControlRecord> = {};
  private allControlRecordsListeners: ((records: ControlRecord[]) => void)[] =
    [];
  private unsubscribeAllControl: Unsubscribe | undefined;
  private debounceUpdateControlListeners = debounce(
    this.updateControlListeners,
    1000,
    { leading: false, trailing: true }
  );
  private pilloriesCache: PilloryTopic[] | undefined;
  private unsubscribePillory: (() => void) | undefined;
  private pilloriesChangedListeners: (() => void)[] = [];
  private ownerProfilesCache: IOwnerProfileSession[] = [];
  private ownerProfilesListeners: ((
    ownerProfiles: IOwnerProfileSession[]
  ) => void)[] = [];
  private unsubscribeOwnerProfiles: Unsubscribe | undefined;

  protected updateControlListeners(): void {
    if (this.allControlRecordsListeners.length > 0)
      this.allControlRecordsListeners.forEach((listener) =>
        listener(Object.values(this.allControlRecords))
      );
  }
  public unsubscribeToAllControlRecords = (
    listener?: () => void
  ): void => {
    if (listener) unsubscribe(this.allControlRecordsListeners, listener);
    else this.allControlRecordsListeners = []; // in admin mode changing the selected row will want to unsubscribe from all the child views
  };

  public subscribeToAllControlRecords = (listener: () => void): void => {
    subscribe(this.allControlRecordsListeners, listener);
  };
  private onAllControlsChanged = (record: ControlRecord): void => {
    if (!record) return;
    if (!this.allControlRecords) this.allControlRecords = {};
    this.allControlRecords[record.userid] = record;
    this.debounceUpdateControlListeners();
  };
  /**
   * required for the pillories button on startup
   */
  private get pillories() {
    if (!this.loggedIn) return [];
    if (!this.pilloriesCache) {
      this.pilloriesCache = [];
      this.unsubscribePillory = this.database.snapAndSubPillory(
        this.onPilloryChanged
      );
    }
    return this.pilloriesCache;
  }

  /**
   * required for the votes button on startup
   */
  private get votingTasks() {
    if (!this.loggedIn) return [];
    if (!this.votingTasksCache) {
      this.votingTasksCache = [];
      this.unsubscribeVotingTasks = this.database.snapAndSubVoteTasks(
        this.onVotingTasks,
        v1()
      );
    }
    return this.votingTasksCache;
  }

  public dispose = (): void => {
    this.unsubscribeAll();
    if (this.unsubscribeVotingTasks) this.unsubscribeVotingTasks();
    if (this.unsubscribeAllControl) this.unsubscribeAllControl();
    if (this.unsubscribePillory) this.unsubscribePillory();
    if (this.unsubscribeOwnerProfiles) this.unsubscribeOwnerProfiles();
  };

  public unsubscribeAll = (): void => {
    this.votingTasksChangedListeners = [];
    this.pilloriesChangedListeners = [];
    this.ownerProfilesListeners = [];
    if (this.unsubscribeAllControl) this.unsubscribeAllControl();
  };

  // called by subscribers to listen to changes to votingTasks
  public subscribeToVotingTasks = (listener: () => void): void => {
    subscribe(this.votingTasksChangedListeners, listener);
  };
  // called by subscribers to listen to changes to votingTasks
  public unsubscribeToVotingTasks = (listener: () => void): void => {
    unsubscribe(this.votingTasksChangedListeners, listener);
  };

  /**
   * called when we initially snapshot all voting tasks on start up
   * @param newTasks all votes avaiable in the system
   */
  private onVotingTasks = (task: string): void => {
    if (!task) return;
    if (!this.votingTasksCache) this.votingTasksCache = [];
    const voteTask = new VoteTask(decrypt<IVoteTask>(task, {} as IVoteTask));
    const idx = this.votingTasksCache.findIndex((t) => t.id === voteTask.id);
    if (idx > -1) this.votingTasksCache[idx] = voteTask;
    else this.votingTasksCache.push(voteTask);

    if (this.votingTasksChangedListeners.length > 0) {
      this.votingTasksChangedListeners.forEach((listener) => listener());
    }
  };

  // eslint-disable-next-line require-await
  public findItemImage = async (
    item: IOptionSupport & IPictureItem,
    defaultImage: string
  ): Promise<string> => {
    const latestItem = item as IOptionSupport & IPictureItem & INamedItem;
    if (
      latestItem &&
      latestItem.options &&
      Array.isArray(latestItem.optionImages) &&
      item.selectedCondition
    ) {
      const idx = latestItem.options.findIndex(
        (i) =>
          item.selectedCondition &&
          item.selectedCondition.replace(
            ` ${(item as any as INamedItem).name}`,
            ""
          ) === i
      );
      if (idx > -1 && latestItem.optionImages[idx]) {
        return this.getDisplayImage(latestItem.optionImages[idx], defaultImage);
      }
    }
    return this.getDisplayImage(latestItem ?? item, defaultImage);
  };

  private onPilloryChanged = (vals: Record<string, string>): void => {
    if (!vals) return;
    this.pilloriesCache = Object.values(vals as Record<string, string>).map(
      (c) => new PilloryTopic(decrypt<IPilloryTopic>(c, {} as IPilloryTopic))
    );
    if (this.pilloriesChangedListeners.length > 0) {
      this.pilloriesChangedListeners.forEach((listener) => listener());
    }
  };
  private ownerProfilesChangedCallback = (snapshot: any): void => {
    this.ownerProfilesCache = [];
    const ownerProfiles = snapshot as Record<string, IOwnerProfileSession>;
    if (ownerProfiles) {
      this.ownerProfilesCache = Object.keys(ownerProfiles).map((tk) => {
        const profile = decrypt(
          ownerProfiles[tk] as unknown as string,
          {}
        ) as IOwnerProfileSession;
        return profile;
      });
    }
    if (this.ownerProfilesListeners.length > 0) {
      this.ownerProfilesListeners.forEach((listener) =>
        listener(this.ownerProfiles)
      );
    }
  };

  private get ownerProfiles() {
    return this.ownerProfilesCache;
  }

  // called by subscribers to listen to changes to pillories
  public subscribeToPillory = (listener: () => void): void => {
    subscribe(this.pilloriesChangedListeners, listener);
  };
  // called by subscribers to listen to changes to pillories
  public unsubscribeToPillory = (listener: () => void): void => {
    unsubscribe(this.pilloriesChangedListeners, listener);
  };

  public getAllControlRecords = (): ControlRecord[] => {
    return Object.values(this.allControlRecords);
  };

  public getState<T>(key: string, defaultVal: T): T {
    return defaultVal;
  }
  public updateCommunityState = (filters: any) => ({});
  public isLocked = () => true;
  public getBottomMembers = () => [];
  public getTopOwners = () => [];
  public getTopMembers = () => [];
  public isSystemLoggedOn = () => false;
  public isSettingsLoaded = () => true;
  public isControlLoaded = () => true;
  public canShowSummary = () => true;
  public getOwnerProfileSummary = () => undefined;
  public getMemberOwnerProfileSummary = () => undefined;

  public getActiveSessions = (
    includeOwners?: boolean
  ): Record<string, ControlRecord> => {
    const activeKeys = this.getAllControlRecords().filter(
      (u) =>
        this.database.getSystemUser()?.userid !== u.userid &&
        (includeOwners ||
          u.role === ProfileRole.Member ||
          u.role === ProfileRole.Switch) &&
        isAfter(u.lastUpdateTime, startOfYesterday())
    );
    const activeUsers: Record<string, ControlRecord> = {};
    activeKeys.forEach((u) => {
      activeUsers[u.userid] = u;
    });
    return activeUsers;
  };

  public getProfile = (userid: string): ControlRecord | undefined => {
    if (userid && userid.endsWith(memberOwnerKeySuffix))
      userid = userid.replace(memberOwnerKeySuffix, "");
    return this.allControlRecords[userid];
  };

  public getVotes = () => this.votingTasks;
  public getControl = () =>
  ({
    userid: this.cookiesService.userid ?? import.meta.env.VITE_GUEST_NAME,
  } as unknown as ControlRecord);
  public getPillories = () => this.pillories;
  public showSummary = () => {
    return;
  };
  public subscribeToLoaded = (cb: () => void) => {
    return;
  };
  public subscribeToOwnerProfiles = (listener: () => void): void => {
    subscribe(this.ownerProfilesListeners, listener);
  };

  public unsubscribeToOwnerProfiles = (listener: () => void): void => {
    unsubscribe(this.ownerProfilesListeners, listener);
  };

  public unsubscribeToLoaded = (cb: () => void) => {
    return;
  };
  public subscribeToViewChanges = (cb: () => void) => {
    return;
  };
  public unsubscribeToViewChanges = (cb: () => void) => {
    return;
  };
  public subscribeToControlRecord = (cb: () => void) => {
    return;
  };
  public isSettingsOwner = () => false;
  public isLoggedOnUserSession = () => {
    return false;
  };
  public unsubscribeToControlRecord = (cb: () => void) => {
    return;
  };
  public getCategories = (onlyVisible?: boolean): string[] => {
    return [];
  };

  public getSystemUser = (): IControl | undefined => {
    return {} as IControl;
  };

  public getAllProfiles = (showAll: boolean): IOwnerProfileSession[] => {
    const owners = Object.values(this.ownerProfiles).filter(
      (t) => showAll || !t.hidden
    );
    return owners;
  };

  public updateState<T>(key: string, value: T): void {
    return;
  }

  public votePilloryUp = async (
    pillory: PilloryTopic,
    option?: string
  ): Promise<void> => {
    const hasError = pillory.voteUp(
      this.getControl().userid,
      this.cookiesService,
      option
    );
    if (hasError && hasError.message) {
      this.showMessage({
        message: hasError.message,
        error: true,
        persist: true,
        notificationType: NotificationType.ErrorInfo,
      });
      return;
    }
    await this.database.updatePilloryTopic(pillory);
    this.updateCookiesWithVote(pillory.id);
    if (this.pilloriesChangedListeners.length > 0) {
      this.pilloriesChangedListeners.forEach((listener) => listener());
    }
    handleDiscordPilloryTickerUp(this, pillory);
  };

  public votePilloryDown = async (pillory: PilloryTopic): Promise<void> => {
    const hasError = pillory.voteDown(
      this.getControl().userid,
      this.cookiesService
    );
    if (hasError && hasError.message) {
      this.showMessage({
        message: hasError.message,
        error: true,
        persist: true,
        notificationType: NotificationType.ErrorInfo,
      });
      return;
    }
    this.updateCookiesWithVote(pillory.id);
    await this.database.updatePilloryTopic(pillory);
    if (this.pilloriesChangedListeners.length > 0) {
      this.pilloriesChangedListeners.forEach((listener) => listener());
    }
    handleDiscordPilloryTickerDown(this, pillory);
  };

  public voteTaskUp = (vote: VoteTask): void => {
    let hasError;
    if (vote.lovenseToyId) {
      const { lovenseProfile, voteCount, lovenseToyId, getVoter } = vote;
      const voter = getVoter(this.getControl().userid);
      const multiplier = voter ? voter + 1 : 1;
      const cmd = new LovenseCommand(
        import.meta.env.VITE_LOVENSE_API_KEY as string,
        vote.userid,
        lovenseToyId,
        (voteCount || 1) * 60 * multiplier,
        "Preset",
        lovenseProfile
      );
      void pushLovense(cmd);
      hasError = vote.nudge(this.getControl().userid);
    } else {
      hasError = vote.voteUp(this.getControl().userid, this.cookiesService);
    }
    if (hasError && hasError.message) {
      this.showMessage({
        message: hasError.message,
        error: true,
        persist: true,
        notificationType: NotificationType.ErrorInfo,
      });
      return;
    }

    this.updateCookiesWithVote(vote.key);
    void this.database.updateVoteTask(vote);
    if (this.votingTasksChangedListeners.length > 0) {
      this.votingTasksChangedListeners.forEach((listener) => listener());
    }
    handleDiscordVoteTickerUp(this, vote);
  };

  public voteTaskDown = (vote: VoteTask, isOwner?: boolean): void => {
    const hasError = vote.voteDown(
      this.getControl().userid,
      this.cookiesService,
      false
    );
    if (hasError && hasError.message) {
      this.showMessage({
        message: hasError.message,
        error: true,
        persist: true,
        notificationType: NotificationType.ErrorInfo,
      });
      return;
    } else {
      this.updateCookiesWithVote(vote.key);
      void this.database.updateVoteTask(vote);
    }
    if (this.votingTasksChangedListeners.length > 0) {
      this.votingTasksChangedListeners.forEach((listener) => listener());
    }
    handleDiscordVoteTickerDown(this, vote);
  };

  private updateCookiesWithVote = (voteid: string) => {
    let { voteItems } = this.cookiesService;
    if (!voteItems) voteItems = [];
    if (!voteItems.includes(voteid)) voteItems.push(voteid);
    this.cookiesService.setVoteItems(voteItems);
  };

  public async sendMessage(
    message: string,
    subject: string,
    fromUserid: string,
    fromUsername: string,
    isSystemAudit: boolean,
    sentReceiptRequired: boolean,
    messageType: MessageType,
    recipients: IUserRecord[],
    ignoreSelfOwned?: boolean,
    state?: any
  ): Promise<void> {
    void this.database.sendMessage(
      message,
      subject,
      fromUserid,
      fromUsername,
      isSystemAudit,
      sentReceiptRequired,
      messageType,
      recipients,
      ignoreSelfOwned,
      state
    );
  }

  public getOwnerHistory = async (
    profile: IControl
  ): Promise<IOwnerHistory[]> => {
    return [];
  };

  public getOwnedSessions = (): ControlRecord[] => {
    return [];
  };
  public getCurrentCommunityProfile = (): ControlRecord => {
    return {} as ControlRecord;
  };

  public getMemberCommunityProfile(
    user: ControlRecord | undefined
  ): ControlRecord {
    if (!user) return user as unknown as ControlRecord;
    const role = this.getCommunityRole(user, this.ownerProfiles);
    const ownerProfile = this.ownerProfiles.find(
      (p) => p && p.userid && p.userid.includes(user.userid)
    );
    if (role === ProfileRole.Owner && ownerProfile) {
      return this.convertOwnerProfileToCommunityProfile(user, ownerProfile);
    } else if (role === ProfileRole.Switch && ownerProfile) {
      return this.convertSwitchProfileToCommunityProfile(user, ownerProfile);
    }
    return new ControlRecord({
      ...user,
      role: role,
      profileImage: getProfileImage(user),
    });
  }
}

export default GuestSettings;
